import { Field } from '@/api/types/crm/crm.types'
import { isValidLeadLinkValue } from '@pages/Admin/CRM/utils/leadFields'
import { z, ZodSchema } from 'zod'
import {
  isValidMaxAccountValue,
  isValidTelegramAccountValue,
  MAX_ACCOUNT_FIELD_UNIQUE_NAME,
  TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME,
} from '@pages/Admin/CRM/utils/messengerAccountFields'

const CRM_TEXTAREA_MAX_LENGTH = 10000

// Валидация по unique_name
const uniqueNameValidations: Record<string, ZodSchema> = {
  [TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME]: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional()
    .refine(
      (val) => val === undefined || isValidTelegramAccountValue(val),
      'Укажите https://t.me/username или числовой id',
    ),
  [MAX_ACCOUNT_FIELD_UNIQUE_NAME]: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional()
    .refine(
      (val) => val === undefined || isValidMaxAccountValue(val),
      'Укажите https://max.ru/u/уникальный-код или числовой id',
    ),
  // NAME: z
  //   .string({
  //     required_error: 'Обязательное поле',
  //   })
  //   .trim()
  //   .min(1, 'Обязательное поле')
  //   .max(100, 'Максимум 100 символов'),
  // если добавляем сюда, проверка по typeValidations отключается
}

// Валидация по type
const typeValidations: Record<string, ZodSchema> = {
  number: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional()
    .transform((val) => (val !== undefined ? Number(val) : undefined))
    .refine((val) => val === undefined || !isNaN(val), 'Должно быть числом'),

  textfield: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional()
    .refine(
      (val) => val === undefined || val.length <= 200,
      'Максимум 200 символов',
    ),

  textarea: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional()
    .refine(
      (val) => val === undefined || val.length <= CRM_TEXTAREA_MAX_LENGTH,
      `Максимум ${CRM_TEXTAREA_MAX_LENGTH} символов`,
    ),
  select: z.string().optional(),
  datetime: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional()
    .refine(
      (val) => val === undefined || !isNaN(Date.parse(val)),
      'Неверный формат даты',
    ),
  phone_number: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional()
    .refine(
      (val) => val === undefined || /^\+?\d+$/.test(val),
      'Телефон: цифры, допускается «+» в начале',
    ),

  link: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional()
    .refine(
      (val) => val === undefined || isValidLeadLinkValue(val),
      'Укажите корректную ссылку (http:// или https://)',
    ),
}
// Поля которые не должны попасть в форму, или не должны проверяться
export const ignoreLeadFields = ['ID', 'STATUS', 'DATEL', 'DATEZ', 'DATEN']

type Data = Record<string, string>

export const validateForm = (
  formData: Data,
  fields: Field[],
  needCheckIsRequired: boolean = true,
): true | Data => {
  const fieldErrors: Record<string, string> = {}

  // Сначала проверяем is_required поля
  if (needCheckIsRequired) {
    fields.forEach((field) => {
      if (field.is_active && field.is_required) {
        const rawValue = formData[field.unique_name]
        const value =
          rawValue === null || rawValue === undefined
            ? ''
            : String(rawValue).trim()
        if (value === '') {
          fieldErrors[field.unique_name] = 'Обязательное поле'
        }
      }
    })
  }

  const schemaObject: Record<string, ZodSchema> = {}

  // Проверяем по uniqueNameValidations или typeValidations
  fields.forEach((field) => {
    if (field.is_active) {
      const schema =
        uniqueNameValidations[field.unique_name] ||
        typeValidations[field.type] ||
        z.string().optional()
      schemaObject[field.unique_name] = schema
    }
  })

  const schema = z.object(schemaObject)
  const result = schema.safeParse(formData)

  if (result.success && Object.keys(fieldErrors).length === 0) return true

  if (!result.success) {
    result.error.errors.forEach((err) => {
      const path = err.path[0] as string
      fieldErrors[path] = err.message
    })
  }

  return fieldErrors
}
